/* eslint-disable react/prop-types */
/**
 * MobileTracker — single-vertical-list live view that renders tickets per
 * the user's spec:
 *   • Grouped by ticket (no tabs)
 *   • Only legs whose games have STARTED are shown by name
 *   • Pregame legs are counted — "N legs awaiting kickoff" — but hidden
 *   • Player NAMES + yardage NUMBERS are colored (not just pills)
 *   • Explicit text label beside the color (a11y)
 *   • Three timestamps are shown separately:
 *       1. Last check time (America/Los_Angeles)
 *       2. Source update time (when supplied)
 *       3. Game quarter/clock
 */
import { Headshot } from './Headshot';
import { LiveClock } from './LiveClock';
import { LegTags } from './OnCourtTag';
import { BackInTag } from './BackInTag';
import { FoulTroubleBar } from './FoulTag';
import { GameInjuryBar } from './GameInjuryBar';
import InjuryDetails from './InjuryDetails';
import { legTimePT, ptText } from '../lib/ptTime';
import ShootingLine from './ShootingLine';
import TicketMoney from './TicketMoney';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2, Trophy, Clock, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import TicketBoard from './TicketBoard';
import MiniBoard from './MiniBoard';
import TicketTiles from './TicketTiles';
import RepeatedPicks from './RepeatedPicks';
import Possession, { LiveLine, RedZoneBar, ticketInRedZone } from './Possession';
import BulkDeleteBar from './BulkDeleteBar';
import DeadStamp from './DeadStamp';
import PlatformBadge from './PlatformBadge';
import TeamLogo, { LegTeam, multiTeam } from './TeamLogo';
import ArchiveView from './ArchiveView';
import { ticketLabel } from './SharedPicks';
import { ticketDate, isVoid, uploadedAt, missingLegs, offDayLegs, legKey, sortedLegs, compareTickets, blinkClass, groupByWindow } from './ticketDate';

// Tickets grouped by when they settle: morning / midday / night (Pacific), newest day first.
export const WindowGroups = ({ tickets, mode, render }) => (
  <div className="space-y-3" data-testid="window-groups">
    {groupByWindow(tickets, mode).map((g) => (
      <section key={g.key} data-testid={`window-${g.key}`}>
        <p className="mb-1.5 px-1 text-xs font-bold text-zinc-200" data-testid={`window-label-${g.key}`}>{g.label} · {g.list.length} ticket{g.list.length === 1 ? '' : 's'}</p>
        {render(g.list)}
      </section>
    ))}
  </div>
);
import { SortBar, useTicketSort } from './SortBar';
import SettleButtons from './SettleButtons';
import TrackerLegend from './TrackerLegend';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const AUTO_REFRESH_SEC = 20;

// Map backend color_token → Tailwind text classes.  Explicit label is
// rendered alongside so meaning does NOT depend only on color.
const COLOR_TOKEN = {
  neutral: { text: 'text-zinc-200', dim: 'text-zinc-500', label: '' },
  yellow:  { text: 'text-amber-300', dim: 'text-amber-400/70', label: 'Below target' },
  orange:  { text: 'text-orange-300', dim: 'text-orange-400/80', label: 'Trailing' },
  blue:    { text: 'text-sky-300',  dim: 'text-sky-400/70',  label: 'Winning' },
  purple:  { text: 'text-purple-300', dim: 'text-purple-300/80', label: '50% of goal' },
  green:   { text: 'text-emerald-300', dim: 'text-emerald-400/70', label: 'Hit' },
  red:     { text: 'text-red-300',  dim: 'text-red-400/70',  label: 'Miss' },
};

// Whole-row tint so up/down is visible at a glance.
const LEG_TINT = {
  neutral: 'bg-zinc-950/70 border border-zinc-800',
  yellow: 'bg-yellow-400/5 border-2 border-yellow-400',
  orange: 'bg-orange-500/15 border-2 border-orange-500',
  blue: 'bg-sky-500/15 border-2 border-sky-400',
  purple: 'bg-purple-500/20 border-2 border-purple-400',
  green: 'bg-emerald-500/15 border-2 border-emerald-400',
  red: 'bg-red-500/20 border-2 border-red-500',
};

// Live tie → yellow; trailing team → orange (from the backend).
const legColor = (leg) => (leg.performance_status === 'LIVE_TIED' ? 'yellow' : leg.color_token || 'neutral');

// Mapping of performance_status → human label (fallback when color_token absent)
const PERF_LABEL = {
  PREGAME:        'Game not started',
  LIVE_BELOW:     'Below target',
  LIVE_REACHED:   'HIT ✓ — mark reached',
  LIVE_PENDING:   'In play',
  RECORDED_HIT:   'TD recorded (subject to review)',
  FINAL_HIT:      'Final hit',
  FINAL_MISS:     'Final miss',
  LIVE_LEADING:   'Leading',
  LIVE_TRAILING:  'Trailing',
  LIVE_TIED:      'Tied',
  FINAL_WIN:      'Final win',
  FINAL_LOSS:     'Final loss',
  UNVERIFIED:     'Not verified',
  VOID:           'Void',
};

function fmtPT(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }) + ' PT';
  } catch { return '—'; }
}

function hasStarted(leg) {
  const s = (leg.performance_status || '').toUpperCase();
  if (s && s !== 'PREGAME') return true;
  const legacy = (leg.status || '').toLowerCase();
  if (legacy === 'live' || legacy === 'won' || legacy === 'lost' || legacy === 'push') return true;
  // Unmatched legs are shown too — the user needs to see the diagnostic
  if (leg.match_status === 'unmatched') return true;
  return false;
}

function isLost(leg) {
  const s = (leg.performance_status || '').toUpperCase();
  return s === 'FINAL_MISS' || s === 'FINAL_LOSS' || (leg.status || '').toLowerCase() === 'lost';
}

function isWon(leg) {
  const s = (leg.performance_status || '').toUpperCase();
  return s === 'FINAL_HIT' || s === 'FINAL_WIN' || (leg.status || '').toLowerCase() === 'won';
}

function kickoffLabel(leg) {
  if (leg.event_date || leg.game_time) return legTimePT(leg);
  if (!leg.game_date) return 'Not started';
  const [y, m, d] = leg.game_date.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function LegRow({ leg, offDay, multi, ticketId }) {
  const [inj, setInj] = useState(false);
  const token = COLOR_TOKEN[legColor(leg)] || COLOR_TOKEN.neutral;
  const started = hasStarted(leg);
  const progress = leg.performance_status === 'LIVE_BELOW' || leg.performance_status === 'LIVE_PENDING'
    ? { purple: '50% of goal', blue: '75% of goal' }[leg.color_token] : null;
  const perfLabel = started ? (progress || PERF_LABEL[leg.performance_status] || token.label) : 'Not started';
  const isPlayerProp = (leg.bet_type || '').toLowerCase() === 'player_prop';
  const isTeamLeg = ['moneyline', 'spread'].includes((leg.bet_type || '').toLowerCase());

  // Compose main caption
  const name = isPlayerProp
    ? (leg.player_name || leg.selection || 'Player')
    : (leg.selection || '—');

  const market = (leg.stat_target || leg.bet_type || '').replace(/_/g, ' ');
  const line = leg.line;
  const current = leg.current_value;
  const target = leg.required_target;
  const remaining = leg.remaining_needed;

  return (
    <div className={`relative rounded-lg px-3 py-2 ${LEG_TINT[legColor(leg)] || LEG_TINT.neutral}${blinkClass(leg.blink, legColor(leg))}`}
         data-testid={`mobile-leg-${leg.id}`} data-blink={leg.blink ? 'true' : undefined} data-color={legColor(leg)}>
      {offDay && <p className="mb-1 inline-block rounded bg-amber-500/25 px-1.5 text-[10px] font-bold text-amber-200" data-testid={`leg-offday-${leg.id}`}>Plays {offDay} — different day than the rest of this ticket</p>}
      <BackInTag leg={leg} />
      <RedZoneBar leg={leg} />
      <LiveLine leg={leg} className="text-[11px] mb-0.5" />
      {/* Line 1 — colored player/team name + market */}
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          {isPlayerProp && <Headshot leg={leg} size={34} testId={`leg-headshot-${leg.id}`} />}
          <TeamLogo leg={leg} size={22} />
          <span className={`font-semibold text-sm ${token.text}`}
                data-testid={`leg-name-${leg.id}`}>
            {name}{isPlayerProp && leg.jersey && <span className="ml-1 opacity-80" data-testid={`leg-jersey-${leg.id}`}>(#{leg.jersey})</span>}
          </span>
          {isPlayerProp && current !== undefined && current !== null && (
            <span className={`font-mono text-base font-bold ${token.text}`}
                  data-testid={`leg-current-${leg.id}`}>
              — {current}
            </span>
          )}
          {isPlayerProp && market && (
            <span className="text-xs text-zinc-400">{market}</span>
          )}
        </div>
        {multi && !isPlayerProp && <LegTeam leg={leg} size={22} />}
        {/* Explicit text label — a11y supplement to color */}
        <span className={`text-[11px] font-medium ${token.dim}`}
              data-testid={`leg-perf-${leg.id}`}>
          {perfLabel}
        </span>
      </div>
      <ShootingLine leg={leg} className="mt-0.5 text-[11px] text-zinc-300" />
      <LegTags leg={leg} />

      {/* Line 2 — market details for player prop */}
      {isPlayerProp && (line !== undefined && line !== null) && (
        <p className="text-xs text-zinc-400 mt-0.5">
          {/\bUNDER\b|\bU\s*\d/i.test(leg.selection || '') ? 'Under' : 'Over'} {line}
          {target != null && <> • {/\bUNDER\b|\bU\s*\d/i.test(leg.selection || '') ? 'Max' : 'Needs'} <span className="text-white font-semibold">{target}</span></>}
          {remaining != null && remaining > 0 && (
            <> • <span className="text-amber-300 font-semibold">{remaining}</span> more</>
          )}
        </p>
      )}

      {/* Team-win: show both teams + score */}
      {(leg.away_team || leg.home_team || leg.away_abbr) && !started && (
        <div className="flex items-center justify-between mt-1 text-xs" data-testid={`leg-pregame-${leg.id}`}>
          <span className="text-zinc-400">{leg.away_abbr || leg.away_team || '?'} <span className="text-zinc-600">@</span> {leg.home_abbr || leg.home_team || '?'}</span>
          <span className="text-[11px] text-zinc-500">{kickoffLabel(leg)}</span>
        </div>
      )}
      {(leg.away_team || leg.home_team || leg.away_abbr) && started && (
        <div className="flex items-center justify-between mt-1 text-xs">
          <span className="text-zinc-300">
            {leg.away_abbr || leg.away_team || '?'} <span className="font-mono text-white font-bold">{leg.live_score_away ?? 0}</span>
            <span className="mx-1 text-zinc-600">—</span>
            <span className="font-mono text-white font-bold">{leg.live_score_home ?? 0}</span> {leg.home_abbr || leg.home_team || '?'}
          </span>
          <span className="text-[11px] text-amber-300/80" data-testid={`leg-clock-${leg.id}`}>
            {leg.clock_running ? <LiveClock leg={leg} testid={`leg-clock-tick-${leg.id}`} /> : ptText(leg.live_period)}
          </span>
        </div>
      )}
      {started && <Possession leg={leg} />}

      {/* Line 3 — status note + freshness */}
      {started && (leg.result_note || leg.last_check_at) && (
      <div className="mt-1 flex items-center justify-between text-[10px] text-zinc-500 gap-2 flex-wrap">
        <span className="italic">{leg.result_note || ''}</span>
        {leg.last_check_at && (
          <span data-testid={`leg-last-check-${leg.id}`}>
            Checked {fmtPT(leg.last_check_at)}
          </span>
        )}
      </div>
      )}
      {leg.bench_note && !leg.availability && <div className="mt-1 text-[11px] text-zinc-400" data-testid={`leg-bench-${leg.id}`}>{leg.bench_note}</div>}
      <GameInjuryBar leg={leg} big onClick={() => setInj(true)} />
      <FoulTroubleBar leg={leg} big />
      {leg.availability && !(leg.game_injury?.stage && !leg.game_injury?.final) && (
        <button type="button" onClick={() => setInj(true)}
             className={`mt-1 w-full text-left text-[11px] font-bold rounded px-2 py-1 underline decoration-dotted ${leg.availability.level === 'out' ? 'bg-red-600 text-white' : 'bg-orange-500 text-black'}`}
             data-testid={`leg-injury-${leg.id}`}>
          ⚠ {leg.availability.text}{leg.availability.reported_at ? ` · ${fmtPT(leg.availability.reported_at)}` : ''} · details
        </button>
      )}
      {inj && <InjuryDetails ticketId={ticketId} leg={leg} open={inj} onClose={() => setInj(false)} />}
      {leg.match_status === 'unmatched' && (
        <div className="mt-1 text-[10px] rounded bg-amber-500/10 border border-amber-500/30 px-2 py-1 text-amber-300"
             data-testid={`leg-unmatched-${leg.id}`}>
          ⚠️ Awaiting live ESPN match — this leg isn't tied to a game yet.
          Fill in both team names on edit to force a match.
        </div>
      )}
    </div>
  );
}


function TicketCard({ ticket, onChanged, onArchive, onSettle }) {
  const remove = async () => {
    try {
      await axios.delete(`${API}/parlays/${ticket.id}`);
      toast.success('Moved to Settled — it still counts in your record');
      onChanged();
    } catch (e) { toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`); }
  };
  const legs = ticket.legs || [];
  const startedLegs = legs.filter(hasStarted);
  const pregameCount = legs.length - startedLegs.length;
  const hits = legs.filter(isWon).length;
  const miss = legs.filter(isLost).length;
  const liveReached = legs.filter((l) =>
    (l.performance_status || '').toUpperCase() === 'LIVE_REACHED'
  ).length;
  const pending = legs.length - hits - miss;

  const man = ticket.manual_result;
  const lostT = man ? man === 'lost' : (miss > 0 || ticket.status === 'lost');
  const allHit = man ? man === 'won' : !lostT && !missingLegs(ticket) && (ticket.status === 'won' || (legs.some((l) => legColor(l) === 'green') && legs.every((l) => legColor(l) === 'green' || isVoid(l))));
  const stillAliveText = lostT ? 'Lost' : allHit ? 'Won' : 'Still alive';

  return (
    <div className={`relative rounded-xl bg-zinc-900/70 p-3 ${ticketInRedZone(ticket) ? 'border-2 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.6)]' : `border ${
      lostT ? 'border-red-500/40' : allHit ? 'border-emerald-400/60' : 'border-zinc-800'}`
    }`} data-testid={`ticket-card-${ticket.id}`} data-redzone={ticketInRedZone(ticket) ? 'true' : undefined}>
      {lostT && <DeadStamp ticketId={ticket.id} big />}
      {allHit && <DeadStamp ticketId={ticket.id} big variant="won" />}
      <PlatformBadge ticket={ticket} onChanged={onChanged} className="!top-2 !right-2 !w-7 !h-7 !text-sm" />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-zinc-300">
            {ticketLabel(ticket)} · {legs.length}-leg{ticket.sportsbook ? ` ${ticket.sportsbook}` : ''}
          </h3>
          <TicketMoney ticket={ticket} onChanged={onChanged} />
          {onSettle && <div className="mt-1"><SettleButtons ticket={ticket} onSettle={onSettle} /></div>}
          <p className="text-[11px] text-zinc-400 mt-0.5">
            <span className={lostT ? 'text-red-300 font-semibold' : 'text-emerald-300 font-semibold'}>
              {stillAliveText}
            </span>
            <span className="mx-1.5 text-zinc-600">•</span>
            {hits}/{legs.length} hits
            <span className="mx-1.5 text-zinc-600">•</span>
            {liveReached} live targets reached
            <span className="mx-1.5 text-zinc-600">•</span>
            {pending} pending
            {pregameCount > 0 && (
              <>
                <span className="mx-1.5 text-zinc-600">•</span>
                {pregameCount} not started
              </>
            )}
          </p>
        </div>
        <div className="relative z-20 flex shrink-0 items-center gap-1 pr-8">
          <span className="text-[11px] font-semibold text-zinc-300" data-testid={`ticket-date-${ticket.id}`}>{ticketDate(ticket)}<span className="block font-normal text-[9px] text-zinc-500 leading-tight" data-testid={`ticket-uploaded-${ticket.id}`}>{uploadedAt(ticket)}</span></span>
          {missingLegs(ticket) && <span className="text-[9px] text-amber-300 font-semibold" data-testid={`ticket-legcount-warn-${ticket.id}`}>{ticket.leg_count} legs on ticket · {(ticket.legs || []).length} read</span>}
          {(lostT || allHit) && (
            <button type="button" onClick={() => onArchive(ticket, true)} className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10" title="Save to archive" data-testid={`archive-ticket-${ticket.id}`}>
              <Archive className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={remove} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10" title="Delete ticket" data-testid={`delete-ticket-${ticket.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {legs.length > 12 && <p className="text-[10px] text-zinc-500" data-testid={`ticket-long-${ticket.id}`}>{legs.length} legs — scroll inside the ticket to see them all</p>}
        <div className={legs.length > 12 ? 'max-h-[70vh] overflow-y-auto space-y-2 pr-1' : 'contents'} data-testid={`ticket-legs-${ticket.id}`}>
        {sortedLegs(legs).map(([leg, i]) => <LegRow key={legKey(leg, i)} leg={leg} ticketId={ticket.id} offDay={offDayLegs(ticket)[legKey(leg, i)]} multi={multiTeam(ticket)} />)}
        </div>
      </div>
    </div>
  );
}

export default function MobileTracker({ reloadKey = 0 }) {
  const [parlays, setParlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [lastPoll, setLastPoll] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem('sb_tracker_view2') || 'tiles');
  useEffect(() => { localStorage.setItem('sb_tracker_view2', view); }, [view]);
  // Pop up only NEW injury / taken-out news for players on the tickets (first refresh just seeds).
  const seenInjuries = useRef(null);
  const announceInjuries = (list) => {
    const now = new Map();
    list.forEach((p) => (p.legs || []).forEach((l) => {
      if (l.availability) now.set(`${p.id}:${l.id}:${l.availability.text}`, `${l.player_name || l.selection}: ${l.availability.text}`);
    }));
    if (seenInjuries.current) {
      now.forEach((msg, key) => { if (!seenInjuries.current.has(key)) toast.warning(`Injury update — ${msg}`, { duration: 12000 }); });
    }
    seenInjuries.current = new Set(now.keys());
  };
  const [archiveKey, setArchiveKey] = useState(0);
  // Ids the user removed; any in-flight refresh answer that still has them is ignored.
  const gone = useRef(new Set());
  const mutSeq = useRef(0);
  const markGone = (ids) => { ids.forEach((i) => gone.current.add(i)); mutSeq.current += 1; setParlays((ps) => ps.filter((p) => !gone.current.has(p.id))); };
  const keep = (list) => (list || []).filter((p) => !gone.current.has(p.id) && !p.archived);
  const [bulk, setBulk] = useState(null);
  const toggleBulk = (id) => setBulk((b) => { const n = new Set(b); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${bulk.size} ticket${bulk.size === 1 ? '' : 's'}? They move to Settled and still count in your record.`)) return;
    try {
      const r = await axios.post(`${API}/parlays/bulk-delete`, { ids: [...bulk] });
      markGone([...bulk]);
      toast.success(`${r.data.deleted} ticket${r.data.deleted === 1 ? '' : 's'} moved to Settled`);
      setBulk(null); load(); setArchiveKey((k) => k + 1);
    } catch (e) { toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`); }
  };
  const removeTicket = async (t, permanent = false) => {
    if (permanent && !window.confirm(`Delete "${ticketLabel(t)}" for good? It will also drop out of your won/lost record.`)) return;
    try {
      await axios.delete(`${API}/parlays/${t.id}`, { params: permanent ? { permanent: true } : {} });
      markGone([t.id]);
      toast.success(permanent ? 'Ticket deleted for good' : 'Moved to Settled — it still counts in your record');
      load(); setArchiveKey((k) => k + 1);
    } catch (e) { toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`); }
  };
  const settleTicket = async (t, result) => {
    try {
      await axios.post(`${API}/parlays/${t.id}/settle`, { result });
      toast.success(result === 'auto' ? 'Back to automatic grading' : `Marked ${result.toUpperCase()}`);
      load(); setArchiveKey((k) => k + 1);
    } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
  };
  const archiveTicket = async (t, archived) => {
    try {
      await axios.post(`${API}/parlays/${t.id}/archive`, { archived });
      if (archived) markGone([t.id]); else { gone.current.delete(t.id); mutSeq.current += 1; }
      toast.success(archived ? 'Moved to Settled' : 'Restored to board');
      load(); setArchiveKey((k) => k + 1);
    } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
  };

  const load = async () => {
    try {
      const seq = mutSeq.current;
      const res = await axios.get(`${API}/parlays`);
      if (seq !== mutSeq.current) return;
      setParlays(keep(res.data.parlays));
      setLastPoll(new Date().toISOString());
    } catch (_e) { /* silent */ }
  };

  const refreshNow = async () => {
    setPolling(true);
    const seq = mutSeq.current;
    try {
      const res = await axios.post(`${API}/refresh-all`);
      if (seq !== mutSeq.current) await load();
      else if (res.data?.parlays) {
        announceInjuries(res.data.parlays);
        setParlays(keep(res.data.parlays)); setLastPoll(res.data.refreshed_at || new Date().toISOString());
      } else await load();
    } catch (_e) { /* next cycle retries */ } finally { setPolling(false); setNextIn(AUTO_REFRESH_SEC); }
  };
  const [nextIn, setNextIn] = useState(AUTO_REFRESH_SEC);
  useEffect(() => {
    const t = setInterval(() => setNextIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (nextIn === 0 && !polling) refreshNow(); }, [nextIn]);

  // Opening the tracker shows saved tickets instantly, then pulls live ESPN stats right away.
  useEffect(() => { setLoading(true); load().finally(() => { setLoading(false); refreshNow(); }); }, [reloadKey]);
  // Coming back to the app/tab triggers an immediate refresh.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') setNextIn(0); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
  }, []);

  // Best-doing tickets first, worst at the bottom (lost tickets last), then biggest payout; smallest last; dead tickets stay (stamped) at the very bottom until deleted.
  const sortMode = useTicketSort();
  const visible = useMemo(() => [...parlays].sort((a, b) => compareTickets(a, b, sortMode)), [parlays, sortMode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tracker…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="mobile-tracker">
      {/* Freshness bar — 3 timestamps + manual refresh */}
      <div className="rounded-xl bg-zinc-900/70 border border-zinc-800 p-3 flex items-center justify-between gap-3 flex-wrap"
           data-testid="freshness-bar">
        <div className="flex items-center gap-2 text-xs">
          <Clock className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-zinc-400">Last check:</span>
          <span className="text-white font-mono" data-testid="last-check">{fmtPT(lastPoll)}</span>
          <span className="text-zinc-600 mx-1">•</span>
          <span className="text-zinc-400">Source: ESPN public JSON</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">
            unlicensed
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1" data-testid="auto-refresh-countdown">
            <RefreshCw className={`w-3 h-3 ${polling ? 'animate-spin text-amber-400' : ''}`} />
            {polling ? <span className="text-amber-300 font-semibold">Updating live stats…</span>
              : <>Auto-update in <span className="font-mono text-white">0:{String(nextIn).padStart(2, '0')}</span></>}
          </span>
          <button onClick={refreshNow}
                  disabled={polling}
                  className="text-sm bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="refresh-now-btn">
            {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {polling ? 'Refreshing every score…' : `Refresh all scores (${visible.length} ticket${visible.length === 1 ? '' : 's'})`}
          </button>
        </div>
      </div>

      {view !== 'archive' && <RepeatedPicks tickets={visible} />}
      {view !== 'archive' && <div className="px-1"><SortBar /></div>}
      <div className="flex flex-wrap items-center gap-1 px-1" data-testid="tracker-view-toggle">
        {[['tiles', 'All tickets'], ['board', 'All-in-one board'], ['board3', 'Board · 3 per row'], ['mini', 'Mini cards'], ['detailed', 'Detailed cards'], ['archive', 'Settled']].map(([v, label]) => (
          <button key={v} type="button" onClick={() => { setView(v); if (v !== 'tiles') setBulk(null); }} data-testid={`view-${v}`}
            className={`text-xs px-3 py-1 rounded-full border ${view === v ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
        {view !== 'archive' && !bulk && visible.length > 0 && (
          <button type="button" onClick={() => { setBulk(new Set()); setView('tiles'); }} data-testid="bulk-start"
            className="ml-auto text-xs px-3 py-1 rounded-full border border-red-500/50 text-red-300 hover:bg-red-500/10 transition-colors">Select to delete</button>
        )}
      </div>
      {bulk && view === 'tiles' && <BulkDeleteBar tickets={visible} selected={bulk} setSelected={setBulk} onDelete={bulkDelete} onCancel={() => setBulk(null)} />}
      <TrackerLegend />

      {view === 'archive' ? (
        <ArchiveView onDelete={(t) => removeTicket(t, true)} onArchive={archiveTicket} onSettle={settleTicket} reloadKey={archiveKey} />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center">
          <Trophy className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">No active tickets.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Upload a ticket to start tracking.
          </p>
        </div>
      ) : (
        view === 'tiles' ? (
          <TicketTiles tickets={visible} onChanged={load} selected={bulk || undefined} onToggle={toggleBulk}
            renderFull={(p) => <TicketCard ticket={p} onChanged={load} onArchive={archiveTicket} onSettle={settleTicket} />} />
        ) : view === 'board' ? (
          <WindowGroups tickets={visible} mode={sortMode} render={(list) => <TicketBoard tickets={list} onDelete={removeTicket} onArchive={archiveTicket} onSettle={settleTicket} onChanged={load} />} />
        ) : view === 'board3' ? (
          <WindowGroups tickets={visible} mode={sortMode} render={(list) => <TicketBoard layout="three" tickets={list} onDelete={removeTicket} onArchive={archiveTicket} onSettle={settleTicket} onChanged={load} />} />
        ) : view === 'mini' ? (
          <WindowGroups tickets={visible} mode={sortMode} render={(list) => <MiniBoard tickets={list} onDelete={removeTicket} onArchive={archiveTicket} onSettle={settleTicket} onChanged={load} />} />
        ) : (
          <WindowGroups tickets={visible} mode={sortMode} render={(list) => (
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3 items-start" data-testid="tracker-grid">
              {list.map((p) => <TicketCard key={p.id} ticket={p} onChanged={load} onArchive={archiveTicket} onSettle={settleTicket} />)}
            </div>
          )} />
        )
      )}
    </div>
  );
}
